import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Bell } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.6 } };

const upcoming = [
  { title: "I Built an Autonomous AI Marketing Agent", tag: "AI · Case Study", eta: "Coming soon" },
  { title: "Why Most Google Ads Campaigns Waste Budget", tag: "Google Ads", eta: "Coming soon" },
  { title: "CRO Checklist for SaaS Landing Pages", tag: "CRO", eta: "Coming soon" },
  { title: "GA4 Setup Mistakes That Cost Businesses Money", tag: "Analytics", eta: "Coming soon" },
  { title: "AI Workflows Every Marketing Team Should Know", tag: "AI", eta: "Coming soon" },
  { title: "How to Scale Performance Marketing Without Increasing CAC", tag: "Performance", eta: "Coming soon" },
];

export default function Insights() {
  useSEO({
    title: "Insights · Growth Marketing, AI Automation & CRO Playbooks · Shivani Ranawat",
    description: "Practical essays on AI marketing agents, performance marketing, CRO and analytics. Original frameworks and case-driven playbooks from Shivani Ranawat.",
    path: "/insights",
  });
  return (
    <div data-testid="insights-page">
      <section className="pt-32 pb-16 bg-white border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">Insights</div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-7xl tracking-tight text-neutral-900 max-w-4xl leading-[1.02]">
            Frameworks, not fluff.
          </h1>
          <p className="mt-6 max-w-2xl text-neutral-600 text-base md:text-lg">
            Practical essays on AI marketing agents, performance, CRO and analytics — drawn from real engagements, not theory.
          </p>
        </div>
      </section>

      <section className="bg-[#F7F7F5] py-16 md:py-24 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between mb-8">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">Editorial Pipeline · Q1 2026</div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400">{upcoming.length} articles</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcoming.map((a, i) => (
              <motion.article
                key={a.title}
                {...fadeUp}
                data-testid={`insight-${i + 1}`}
                className="rounded-2xl bg-white border border-neutral-200 p-6 hover:border-[#3B82F6] transition group"
              >
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em]">
                  <span className="text-[#3B82F6]">{a.tag}</span>
                  <span className="text-neutral-400">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-6 font-display text-xl text-neutral-900 leading-tight">
                  {a.title}
                </h3>
                <div className="mt-6 pt-4 border-t border-neutral-100 flex items-center justify-between text-xs text-neutral-500">
                  <span className="inline-flex items-center gap-1"><Bell size={12} /> {a.eta}</span>
                  <BookOpen size={14} className="text-neutral-400 group-hover:text-[#3B82F6]" />
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-[#0F1115] text-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Get the first drop</div>
              <div className="mt-2 font-display text-2xl md:text-3xl">Editorial launches Feb 2026.</div>
              <div className="mt-1 text-sm text-white/60">One essay every fortnight. No SEO fluff.</div>
            </div>
            <Link
              to="/contact"
              data-testid="insights-notify-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-neutral-900 px-5 py-3 text-sm font-medium"
            >
              Notify me <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
